import { useOrganizationId } from '@features/user';
import { Skeleton, Typography } from '@mui/material';

import { batchStateLabel } from '../bulkVocabulary';
import { useGetBatchesQuery } from '../store/api';
import { useRequirementMissingCount } from '../store/useRequirementMissingCount';
import type { SubjectLanguage } from '../types';
import { formatRequestDate } from '../vocabulary';

/**
 * The catalog's per-requirement numbers (GDN-4142), each its own small
 * connected component so the table itself stays presentational: the view takes
 * them as nodes, and stories and tests pass plain text instead.
 *
 * A column cell and the expanded row ask the same question with the same
 * arguments, so RTK Query answers both from one request.
 */

const Unknown = () => (
  <Typography variant="body2" color="text.secondary">
    —
  </Typography>
);

interface MissingCountProps {
  definitionId: string;
  active: boolean;
}

/** How many are missing this requirement, as the table's column shows it. */
export const MissingCountCell = ({ definitionId, active }: MissingCountProps) => {
  const { count, isLoading, isError } = useRequirementMissingCount(definitionId);
  if (isLoading) return <Skeleton variant="text" width={24} />;
  // An unknown count is shown as unknown, never as zero: zero reads as
  // "nobody left to chase", which is the one wrong answer that stops work.
  if (isError || count === undefined) return <Unknown />;
  return (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: active && count > 0 ? 'warning.dark' : 'text.secondary',
      }}
    >
      {count.toLocaleString()}
    </Typography>
  );
};

/** The same count as the sentence above the send button. */
export const MissingCountSentence = ({
  definitionId,
  subject,
  canSend = true,
}: {
  definitionId: string;
  subject: SubjectLanguage;
  /** Without it, the sentence stops at the count: there is no send to review. */
  canSend?: boolean;
}) => {
  const { count, isLoading, isError } = useRequirementMissingCount(definitionId);
  if (isLoading) return <Skeleton variant="text" width="80%" />;
  const lead =
    isError || count === undefined
      ? `We could not count the ${subject.plural} missing this.`
      : `${count.toLocaleString()} ${count === 1 ? `${subject.singular} is` : `${subject.plural} are`} missing this.`;
  return (
    <Typography variant="body2">
      {canSend ? `${lead} Review who will receive a request before anything is sent.` : lead}
    </Typography>
  );
};

/**
 * The newest batch for one requirement: when it started, and — in the expanded
 * row — what state it is in, through the same label the progress panel uses
 * (GDN-4140), so a finished batch reads "Finished sending" in both places.
 */
const useLastBatch = (definitionId: string) => {
  const organizationId = useOrganizationId();
  const { data, isLoading, isError } = useGetBatchesQuery(
    { orgId: organizationId ?? '', definitionId, limit: 1 },
    {
      skip: !organizationId,
      // A batch finishes on its own, and nothing invalidates the list when it
      // does. Re-reading whenever a row expands keeps the state line from
      // saying "Sending" about a batch that stopped while the page sat open.
      refetchOnMountOrArgChange: true,
    },
  );
  const batch = data?.items?.[0];
  return {
    isLoading,
    isError,
    when: batch ? formatRequestDate(batch.created_at) : undefined,
    state: batch?.state,
    exists: Boolean(batch),
  };
};

export const LastBulkSendCell = ({ definitionId }: { definitionId: string }) => {
  const last = useLastBatch(definitionId);
  if (last.isLoading) return <Skeleton variant="text" width={48} />;
  if (last.isError) return <Unknown />;
  if (!last.exists) {
    return (
      <Typography variant="body2" color="text.secondary">
        Never
      </Typography>
    );
  }
  // A finished batch is a send, and its date says enough. Anything else —
  // queued, still sending, stopped — names its state, so a batch that never
  // went out does not read as a completed send.
  const state = last.state && last.state !== 'finished' ? batchStateLabel(last.state) : undefined;
  return (
    <Typography variant="body2" color="text.secondary">
      {[last.when, state].filter(Boolean).join(' · ') || batchStateLabel(last.state ?? '')}
    </Typography>
  );
};

export const LastBulkSendDetail = ({ definitionId }: { definitionId: string }) => {
  const last = useLastBatch(definitionId);
  if (last.isLoading || last.isError) return null;
  const text = last.exists
    ? [`Last bulk send ${last.when ?? ''}`.trim(), last.state && batchStateLabel(last.state)]
        .filter(Boolean)
        .join(' · ')
    : 'No bulk sends yet';
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {text}
    </Typography>
  );
};
